<template>
  <div class="test-result__wrapper">
    <div class="test-control__wrapper">
      <div>
        <Text size="h2" class="mb-0 text-dark">
          {{ testName }}
          <Icon
            name="info-circle-fill"
            class="text-primary ml-1 pointer"
            style="width: 22px; height: 22px"
            @click="showInfoModal = true"
          />
        </Text>
        <Alert v-model:show="showInfoModal" close-text="Fermer" color="warning">
          <div class="d-flex flex-column gap-2">
            <Text size="p4" v-html="testDescriptions[testId].description" />
            <template v-if="testDescriptions[testId].actions">
              <Text size="h3"> Actions: </Text>
              <Text size="p4" v-html="testDescriptions[testId].actions" />
            </template>
          </div>
        </Alert>
        <Text
          v-if="['E1', 'J2'].includes(testId)"
          size="p4"
          class="mb-3 text-muted"
        >
          Contrôle applicable uniquement sur la clôture de décembre
        </Text>
        <Text
          v-if="
            [
              'B2',
              'B3.2',
              'B3.3',
              'B6',
              'B7',
              'D1.2',
              'D1.3',
              'I1',
              'J1',
              'J3',
            ].includes(testId)
          "
          size="p4"
          class="mb-3 text-muted"
        >
          Contrôle non applicable dans le cadre de la phase plaquettes
        </Text>
        <Text v-if="['PP'].includes(testId)" size="p4" class="mb-3 text-muted">
          Contrôle applicable uniquement sur les comptes annuels
        </Text>
      </div>
      <RaSwitch
        :disabled="entity?.closed_periods?.includes(quarter_name)"
        class="ml-2 round-switch"
        :class="{ checked: resultOk }"
        v-model:checked="resultOk"
        :label="resultOk ? 'OK' : 'KO'"
      />
    </div>
    <slot name="test-results">
      <component
        :key="testId"
        :is="testResultView"
        :disabled="entity?.closed_periods?.includes(quarter_name)"
        @mounted="handleTestResult"
        ref="testResult"
      />
    </slot>
    <slot name="note">
      <Text size="h5" class="mb-3 text-dark font-weight-bold">
        Commentaires et analyses comptables
      </Text>
      <InputComponent
        :disabled="entity?.closed_periods?.includes(quarter_name)"
        :formField="noteFormField"
      />
      <div class="w-100 d-flex justify-content-end">
        <CButton
          class="mz-button v2-mz-btn btn-primary mt-2 ml-auto"
          @click="() => saveNote(noteFormField.value, 'note')"
          :disabled="entity?.closed_periods?.includes(quarter_name)"
        >
          Save
        </CButton>
      </div>
      <FollowUp
        allowEdit
        allowHtmlContent
        :data="currentNotes"
        v-if="currentNotes.length"
        @edit="
          (content, editId, shouldDelete = false) =>
            handleNoteEdit(content, editId, shouldDelete)
        "
        class="mb-3"
        @click="handleLinksForDownload($event)"
      />
    </slot>
    <slot name="comment">
      <Text size="h5" class="mb-3 text-dark mt-3 font-weight-bold">
        Notes de revue et commentaires
      </Text>
      <InputComponent
        :disabled="entity?.closed_periods?.includes(quarter_name)"
        :formField="commentFormField"
      />
      <div class="w-100 d-flex justify-content-end">
        <CButton
          :disabled="entity?.closed_periods?.includes(quarter_name)"
          class="mz-button v2-mz-btn btn-primary mt-2 ml-auto"
          @click="() => saveNote(commentFormField.value, 'comment')"
        >
          Comment
        </CButton>
      </div>
      <FollowUp
        allowHtmlContent
        :data="currentComments"
        v-if="currentComments.length"
        class="mb-3"
        @click="handleLinksForDownload($event)"
      />
    </slot>
  </div>
</template>

<script>
import { ClosingFileService } from "@/services/closing_file";
import { ActiveUser } from "@/services/user";
import { EntitiesService } from "@/services/entity";
import { defineAsyncComponent, markRaw } from "vue";
import InputComponent from "@/components/InputComponent";
import testDescriptions from "@/constants/testDescriptions";
import { handleLinksForDownload } from "@/utils/request";

const advancedTests = [];

export default {
  name: "TestResultWrapper",
  props: {
    testName: String,
    testId: String,
  },
  components: { InputComponent },
  data() {
    return {
      testDescriptions,
      showInfoModal: false,
      quarter_name: "",
      testResultView: null,
      resultOk: false,
      noteId: null,
      entity: null,
      currentNotes: [],
      currentComments: [],
      noteFormField: {
        name: "note",
        hideLabel: true,
        value: "",
        type: "textarea",
        file: {
          id: 2,
          query: {
            test_id: this.testId,
            entity_id: this.$route.params.entity_id,
          },
        },
      },
      commentFormField: {
        name: "comment",
        hideLabel: true,
        value: "",
        type: "textarea",
        file: {
          id: 2,
          query: {
            test_id: this.testId,
            entity_id: this.$route.params.entity_id,
          },
        },
      },
    };
  },
  async mounted() {
    this.quarter_name = this.$route.params.quarter_name;
    this.loadTestResult();
    this.getTestNotes();
    this.getEntityObj();
  },
  watch: {
    "$route.query.tab"() {
      this.getTestNotes();
    },
    testId: {
      handler() {
        this.loadTestResult();
      },
    },
    resultOk: {
      handler() {
        this.updateTestResult();
      },
    },
  },
  computed: {
    isAdvancedTest() {
      return advancedTests.includes(this.testId);
    },
  },
  methods: {
    handleLinksForDownload,
    async getEntityObj() {
      this.entity = await EntitiesService.getEntity(
        this.$route.params.entity_id,
        true
      );
    },
    async updateTestResult() {
      const { testResult } = this.$refs;
      await ClosingFileService.setResultControlPassed(
        testResult.testId,
        testResult.entityId,
        testResult.quarter_name,
        this.resultOk
      );
    },
    handleTestResult(controlPassed) {
      this.resultOk = controlPassed;
    },
    getTestResultComponent(name = null) {
      name = name || this.testId;
      return markRaw(
        defineAsyncComponent(() =>
          import(
            `@/components/closingFile/testResults/${
              this.isAdvancedTest ? this.testId : "ClosingFileTestResult"
            }.vue`
          )
        )
      );
    },
    loadTestResult() {
      if (!this.testId) return;
      this.testResultView = this.getTestResultComponent();
    },
    handleNoteEdit(content, noteId, shouldDelete = false) {
      this.noteId = noteId;
      if (shouldDelete) return this.saveNote("", noteId, true);
      this.noteFormField.value = !shouldDelete ? content : "";
    },
    async saveNote(content, note_type = "note", shouldDelete) {
      const inputField =
        note_type === "note" ? this.noteFormField : this.commentFormField;
      if (!shouldDelete && inputField.value === "") {
        return alert("Comment can not be empty");
      }
      const noteData = {
        content,
        note_type,
        author: ActiveUser.get()?.email,
        quarter_name: this.$route.params.quarter_name,
        entity_id: this.$route.params.entity_id,
        test_id: this.$route.query.tab,
        note_id: this.noteId,
      };
      await ClosingFileService.saveNote(noteData, shouldDelete);
      this.noteId = null;
      inputField.value = "";
      this[note_type] = "";
      await this.getTestNotes();
    },
    async getTestNotes() {
      const { notes, comments } = await ClosingFileService.getNotes(
        this.$route.params.entity_id,
        this.$route.query.tab,
        this.$route.params.quarter_name
      );
      this.currentNotes = notes;
      this.currentComments = comments;
    },
  },
};
</script>

<style lang="scss">
@mixin disable-switch-animation {
  transition: none !important;
}

.follow-up__header {
  display: none !important;
}

.test-control__wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: 10px;
  .round-switch {
    &.ra-switch {
      min-width: 70px;
      min-height: 70px;
      padding-left: 35px;
      position: relative;
      &.ra-switch input:checked ~ .slider {
        background-color: white;
        border: 5px solid rgba(0, 133, 0, 0.51);
      }
      span:not(.slider) {
        @include disable-switch-animation;
        font-size: 1.35em;
        margin-left: -16px !important;
        position: absolute;
        left: 50%;
        top: 48%;
        transform: translate(-50%, -50%);
        z-index: 3;
      }
      .slider {
        @include disable-switch-animation;
        background-color: white;
        border: 5px solid $secondary;
        width: 64px;
        height: 64px;
        z-index: 1;
        &:after {
          display: none;
        }
      }
      &:after {
        @include disable-switch-animation;
        content: "";
        position: relative;
        top: 2px;
        display: block;
        width: 64px;
        height: 64px;
        border: 5px solid $secondary;
        border-left: none !important;
        border-radius: 0 100% 100% 0;
        z-index: 0;
      }
      &.checked {
        span:not(.slider) {
          color: rgba(0, 88, 0, 0.89);
          left: calc(100% - 17px);
          z-index: 5;
        }
        .slider {
          left: 35%;
          z-index: 4;
        }
        &:after {
          border: 1px solid transparent;
          border-right: none !important;
          background-color: rgba(0, 133, 0, 0.51);
          border-radius: 100% 0 0 100%;
          right: calc(100% - 30px);
        }
      }
    }
  }

  b {
    font-weight: bold;
  }
}
</style>
